/**
 * Settings Component
 * 
 * Customer/User version of the Admin panel.
 * Provides limited functionality:
 * - Organization management (own + vendors only)
 * - NO user management
 * - NO file management
 * - NO admin-only reporting
 * 
 * For Vendor role: Shows VendorSettings with template management
 */

import React, { useState, useEffect, useRef } from 'react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { usePermissions } from '../hooks/usePermissions';
import VendorSettings from './VendorSettings';

// Load Google Maps API (reuse if already loaded)
let googleMapsLoaderPromise = null;
function loadGoogleMaps(apiKey) {
  if (window.google && window.google.maps) return Promise.resolve();
  if (googleMapsLoaderPromise) return googleMapsLoaderPromise;
  googleMapsLoaderPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    const keyParam = apiKey ? `&key=${apiKey}` : '';
    script.src = `https://maps.googleapis.com/maps/api/js?libraries=places${keyParam}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = (e) => reject(e);
    document.head.appendChild(script);
  });
  return googleMapsLoaderPromise;
}

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

export default function Settings() {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const { organizations, warehouses, createOrganization, updateOrganization, deleteOrganization } = useApi();
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [expandedOrgId, setExpandedOrgId] = useState(null);
  
  // Filter organizations to show only Customer's own org and Vendor orgs
  const filteredOrganizations = React.useMemo(() => {
    // Show Customer and Vendor organizations only (not Main/internal)
    return organizations.filter(org => 
      org.type === 'Customer' || org.type === 'Vendor'
    );
  }, [organizations]);
  
  // Get warehouses for an organization
  const getWarehousesForOrg = (orgId) => {
    return Object.values(warehouses).filter(wh => wh.org_id === orgId);
  };
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    type: 'Vendor', // Customers can only add Vendor organizations
    address: '',
    latitude: null,
    longitude: null,
    contact_email: '',
    contact_phone: ''
  });
  
  // Google Places autocomplete
  const addressInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [mapsLoaded, setMapsLoaded] = useState(false);

  // Load Google Maps API
  useEffect(() => {
    loadGoogleMaps(GOOGLE_MAPS_API_KEY)
      .then(() => setMapsLoaded(true))
      .catch(err => console.error('Failed to load Google Maps:', err));
  }, []);

  // Initialize autocomplete when modal opens and maps is loaded
  useEffect(() => {
    if (!showModal || !mapsLoaded || !addressInputRef.current) return;
    if (!window.google || !window.google.maps || !window.google.maps.places) return;

    // Clean up previous autocomplete
    if (autocompleteRef.current) {
      window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
    }

    const autocomplete = new window.google.maps.places.Autocomplete(addressInputRef.current, {
      types: ['address'],
      fields: ['formatted_address', 'geometry']
    });

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (place.geometry && place.geometry.location) {
        setFormData(prev => ({
          ...prev,
          address: place.formatted_address || '',
          latitude: place.geometry.location.lat(),
          longitude: place.geometry.location.lng()
        }));
      }
    });

    autocompleteRef.current = autocomplete;

    return () => {
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [showModal, mapsLoaded]);

  // If user is a vendor, show VendorSettings instead (after all hooks are declared)
  if (isVendor) {
    return <VendorSettings />;
  }

  // Open modal for new organization
  const handleAddNew = () => {
    setEditingOrg(null);
    setFormData({
      name: '',
      type: 'Vendor', // Customers can only add Vendor type
      address: '',
      latitude: null,
      longitude: null,
      contact_email: '',
      contact_phone: ''
    });
    setShowModal(true);
  };

  // Open modal for editing
  const handleEdit = (org) => {
    setEditingOrg(org);
    setFormData({
      name: org.name || '',
      type: org.type || 'Vendor',
      address: org.address || '',
      latitude: org.latitude || null,
      longitude: org.longitude || null,
      contact_email: org.contact_email || '',
      contact_phone: org.contact_phone || ''
    });
    setShowModal(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setShowModal(false);
    setEditingOrg(null);
    setFormData({
      name: '',
      type: 'Vendor',
      address: '',
      latitude: null,
      longitude: null,
      contact_email: '',
      contact_phone: ''
    });
  };

  // Save organization
  const handleSave = async () => {
    if (!formData.name.trim()) {
      alert('Organization name is required');
      return;
    }

    setSaving(true);
    try {
      if (editingOrg) {
        await updateOrganization(editingOrg.id, formData);
        alert('Organization updated successfully!');
      } else {
        await createOrganization(formData);
        alert('Organization created successfully!');
      }
      handleCloseModal();
    } catch (err) {
      alert('Error saving organization: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Delete organization
  const handleDelete = async () => {
    if (!editingOrg) return;
    if (!window.confirm(`Are you sure you want to delete "${editingOrg.name}"? This cannot be undone.`)) return;

    setDeleting(true);
    try {
      await deleteOrganization(editingOrg.id);
      alert('Organization deleted successfully!');
      handleCloseModal();
    } catch (err) {
      alert('Error deleting organization: ' + err.message);
    } finally {
      setDeleting(false);
    }
  };

  // Get type badge color
  const getTypeBadgeColor = (type) => {
    switch (type) {
      case 'Vendor': return 'bg-purple-900/30 text-purple-400';
      case 'Customer': return 'bg-blue-900/30 text-blue-400';
      default: return 'bg-gray-900/30 text-gray-400';
    }
  };

  return (
    <div className={`p-8 h-full overflow-y-auto ${isDark ? 'bg-gray-900 text-gray-100' : 'bg-slate-50 text-gray-900'}`}>
      <header className={`mb-6 border-b pb-6 ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
        <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Settings</h1>
        <p className={`mt-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Manage your organizations and vendors</p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Organization List */}
        <div className="lg:col-span-1 space-y-6">
          <div className={`rounded-xl overflow-hidden border ${isDark ? 'bg-gray-800/30 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
            <div className={`p-4 border-b flex justify-between items-center ${isDark ? 'bg-gray-900/50 border-gray-800' : 'bg-gray-50 border-gray-200'}`}>
              <h3 className={`font-semibold ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>Organizations</h3>
              <button 
                onClick={handleAddNew}
                className="text-blue-500 text-xs hover:text-blue-400 font-medium"
              >
                + Add Vendor
              </button>
            </div>
            <div className={`divide-y max-h-[600px] overflow-y-auto no-scrollbar ${isDark ? 'divide-gray-800' : 'divide-gray-100'}`}>
              {filteredOrganizations.map((org) => {
                const orgWarehouses = getWarehousesForOrg(org.id);
                const isExpanded = expandedOrgId === org.id;
                
                return (
                  <div key={org.id}>
                    <div 
                      className={`p-4 cursor-pointer transition-colors group ${isDark ? 'hover:bg-gray-800/50' : 'hover:bg-gray-50'}`}
                      onClick={() => handleEdit(org)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <div className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{org.name}</div>
                            {orgWarehouses.length > 0 && (
                              <button
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  setExpandedOrgId(isExpanded ? null : org.id); 
                                }}
                                className={`text-xs ${isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
                              >
                                {isExpanded ? '▼' : '▶'} {orgWarehouses.length} warehouse{orgWarehouses.length !== 1 ? 's' : ''}
                              </button>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`px-2 py-0.5 rounded-full text-xs ${getTypeBadgeColor(org.type)}`}>
                              {org.type}
                            </span>
                            <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                              {new Date(org.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          {org.address && (
                            <div className={`text-xs mt-1 truncate max-w-[200px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`} title={org.address}>
                              📍 {org.address}
                            </div>
                          )}
                        </div>
                        <button 
                          className={`opacity-0 group-hover:opacity-100 transition-opacity text-xs ${isDark ? 'text-gray-500 hover:text-blue-400' : 'text-gray-400 hover:text-blue-500'}`}
                          onClick={(e) => { e.stopPropagation(); handleEdit(org); }}
                        >
                          Edit
                        </button>
                      </div>
                    </div>
                    
                    {/* Warehouses List (Expanded) */}
                    {isExpanded && orgWarehouses.length > 0 && (
                      <div className={`border-t ${isDark ? 'bg-gray-900/50 border-gray-800' : 'bg-gray-50 border-gray-100'}`}>
                        {orgWarehouses.map((wh) => (
                          <div 
                            key={wh.id} 
                            className={`px-4 py-2 pl-8 flex items-center gap-2 text-sm border-b last:border-b-0 ${isDark ? 'border-gray-800/50' : 'border-gray-100'}`}
                          >
                            <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>🏭</span>
                            <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>{wh.display_name || wh.name}</span>
                            {wh.gps?.polygon && (
                              <span className="text-xs text-green-500">📍 GPS</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              {filteredOrganizations.length === 0 && (
                <div className={`p-4 text-xs italic ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No organizations found. Add a vendor to get started.</div>
              )}
            </div>
          </div>

          {/* Quick Info Card */}
          <div className={`rounded-xl p-4 border ${isDark ? 'bg-gray-800/30 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
            <h4 className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Need Help?</h4>
            <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              Add your vendor organizations here to manage repairs and asset tracking with them.
              Contact your administrator for additional settings or user management.
            </p>
          </div>
        </div>

        {/* Organization Details / Info Panel */}
        <div className="lg:col-span-2">
          <div className={`rounded-xl p-6 border ${isDark ? 'bg-gray-800/30 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
            <div className="text-center py-12">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
                <span className="text-2xl">⚙️</span>
              </div>
              <h3 className={`text-lg font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Organization Settings</h3>
              <p className={`text-sm mt-2 max-w-md mx-auto ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Manage your vendor organizations from the list on the left. 
                Click on an organization to edit its details, or add a new vendor.
              </p>
              <button 
                onClick={handleAddNew}
                className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium"
              >
                + Add Vendor Organization
              </button>
            </div>

            {/* Account Info */}
            <div className={`border-t mt-6 pt-6 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <h4 className={`text-sm font-medium mb-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Your Account</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className={`rounded-lg p-3 ${isDark ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
                  <div className={`text-xs uppercase mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Email</div>
                  <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{user?.email || 'Not available'}</div>
                </div>
                <div className={`rounded-lg p-3 ${isDark ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
                  <div className={`text-xs uppercase mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Account Type</div>
                  <div className="text-sm text-emerald-500 capitalize">{user?.role || 'Customer'}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Organization Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-2xl w-full max-w-md">
            {/* Modal Header */}
            <div className="p-5 border-b border-gray-700 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-white">
                {editingOrg ? 'Edit Organization' : 'Add Vendor Organization'}
              </h3>
              <button 
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-white text-xl leading-none"
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Organization Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter organization name"
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 placeholder-gray-500 focus:outline-none focus:border-blue-500"
                />
              </div>

              {/* Type (Limited for customers) */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Type
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 focus:outline-none focus:border-blue-500"
                >
                  {/* Customers can only add Vendor or Customer organizations */}
                  <option value="Vendor">Vendor</option>
                  <option value="Customer">Customer</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {formData.type === 'Vendor' && "Vendor organizations for repair and service partners."}
                  {formData.type === 'Customer' && "Customer organizations you work with."}
                </p>
              </div>

              {/* Address with Google Places Autocomplete */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Address
                </label>
                <input
                  ref={addressInputRef}
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="Start typing to search..."
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 placeholder-gray-500 focus:outline-none focus:border-blue-500"
                />
                {formData.latitude && formData.longitude && (
                  <p className="text-xs text-green-400 mt-1">
                    ✓ Coordinates: {formData.latitude.toFixed(6)}, {formData.longitude.toFixed(6)}
                  </p>
                )}
                {!mapsLoaded && (
                  <p className="text-xs text-amber-400 mt-1">Loading Google Maps...</p>
                )}
              </div>

              {/* Contact Email */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Contact Email
                </label>
                <input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact_email: e.target.value }))}
                  placeholder="contact@example.com"
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 placeholder-gray-500 focus:outline-none focus:border-blue-500"
                />
              </div>

              {/* Contact Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Contact Phone
                </label>
                <input
                  type="tel"
                  value={formData.contact_phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact_phone: e.target.value }))}
                  placeholder="+1 (555) 123-4567"
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-md text-gray-100 placeholder-gray-500 focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-5 border-t border-gray-700 flex justify-between">
              <div>
                {editingOrg && (
                  <button
                    onClick={handleDelete}
                    disabled={deleting}
                    className="px-4 py-2 bg-red-900/50 hover:bg-red-900/70 text-red-300 rounded-md text-sm font-medium disabled:opacity-50"
                  >
                    {deleting ? 'Deleting...' : 'Delete'}
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleCloseModal}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-md text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-md text-sm font-medium disabled:opacity-50"
                >
                  {saving ? 'Saving...' : (editingOrg ? 'Update' : 'Create')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

